import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";
import { BookOpen, ArrowRight, Loader2, LibraryBig } from "lucide-react";

const Library = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) navigate("/");
  }, [loading, user, navigate]);

  const { data, isLoading } = useQuery({
    queryKey: ["library"],
    queryFn: async () => (await api.get("/library")).data,
    enabled: !!user,
  });

  if (loading || !user) return <div className="min-h-screen flex justify-center items-center bg-[#F9F9F7]"><Loader2 className="w-8 h-8 animate-spin text-[#9A3412]" /></div>;

  const guides = data?.guides || [];

  return (
    <div className="bg-[#F9F9F7] min-h-screen">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-14">
        <div className="mb-10">
          <p className="text-sm uppercase tracking-widest text-[#9A3412] mb-2">Welcome back, {user.name?.split(" ")[0] || "traveler"}</p>
          <h1 className="font-serif text-5xl font-medium text-stone-900">My Library</h1>
          <p className="text-stone-600 mt-2">Your purchased guides, ready to read on any device.</p>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#9A3412]" /></div>
        ) : guides.length === 0 ? (
          <div className="border border-dashed border-stone-300 bg-white py-24 flex flex-col items-center text-center px-6">
            <LibraryBig className="w-12 h-12 text-stone-300 mb-4" />
            <h3 className="font-serif text-2xl text-stone-800">Your library is empty</h3>
            <p className="text-stone-500 mt-2 max-w-sm">Browse our collection and unlock a country guide to start planning your move.</p>
            <Button asChild className="mt-6 bg-[#9A3412] hover:bg-[#7C2D12] text-white rounded-none px-8"><Link to="/" data-testid="library-browse-btn">Browse guides <ArrowRight className="w-4 h-4 ml-2" /></Link></Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
            {guides.map((g) => (
              <div key={g.slug} data-testid={`library-item-${g.slug}`} className="group border border-stone-200 bg-white">
                <Link to={`/read/${g.slug}`} className="block relative aspect-[3/4] overflow-hidden">
                  <img src={g.cover_url} alt={g.country} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 hero-overlay opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <span className="text-white flex items-center gap-2 text-sm font-medium"><BookOpen className="w-4 h-4" />Read</span>
                  </div>
                  <span className="absolute top-3 left-3 text-2xl">{g.flag}</span>
                </Link>
                <div className="p-4">
                  <h3 className="font-serif text-xl text-stone-900">{g.country}</h3>
                  <div className="flex gap-2 mt-3">
                    <Button asChild size="sm" className="flex-1 bg-[#9A3412] hover:bg-[#7C2D12] text-white rounded-none"><Link to={`/read/${g.slug}`} data-testid={`read-${g.slug}`}>Read</Link></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Library;
